import { useBucket, useExperimentVariables } from '@mc/experiments-react'
import { reportError } from '@mc/next-newrelic'
import { useIsEnterpriseMember } from 'pages/certificates/common/useIsEnterpriseMember'
import { promoFlagKey, SITE_WIDE_PROMO_FLAG } from './flags'
import { CertificatePromoVariablesSchema, type PromoVariables } from './schemas'

// Parse a single flag's raw GrowthBook variables into promo copy, mirroring the
// control/empty gate + zod validation used server-side (getGrowthBookVariables).
const parsePromoVariables = (
  promoFlag: string,
  bucket: string | null | undefined,
  variables: Record<string, unknown> | undefined,
): PromoVariables | null => {
  if (
    bucket === 'control' ||
    !variables ||
    Object.keys(variables).length === 0
  ) {
    return null
  }

  const validated = CertificatePromoVariablesSchema.safeParse(variables)
  if (validated.success) return validated.data

  reportError(validated.error, {
    message: 'Invalid promo variables format',
    promoFlag,
    promoVariables: JSON.stringify(variables),
  })
  return null
}

/** mccerts-2851_v1_core_nav_promo_signal
 * Client-side **site-wide** certificate promo variables (`certificates-promotions`),
 * independent of any cert slug. Null for enterprise or when the site-wide promo is
 * off/malformed. Use this for slug-less surfaces (e.g. global nav); slug-scoped
 * surfaces should use `useCertificatePromoVariables`, which layers the per-slug
 * promo on top of this.
 */
export const useSiteWidePromoVariables = (): PromoVariables | null => {
  const isEnterprise = useIsEnterpriseMember()
  const siteWideBucket = useBucket(SITE_WIDE_PROMO_FLAG)
  const siteWideVariables =
    useExperimentVariables<Record<string, unknown>>(SITE_WIDE_PROMO_FLAG)

  if (isEnterprise) return null

  return parsePromoVariables(
    SITE_WIDE_PROMO_FLAG,
    siteWideBucket,
    siteWideVariables,
  )
}

/**
 * Client-side certificate promo variables, resolved **site-wide-first** to
 * mirror `getPromoVariables` (SSR) and the backend charge precedence:
 * the global `certificates-promotions` payload wins over the per-slug
 * `mccerts-promo-{slug}` promo. Both flags are read unconditionally (hooks
 * rules) and gated the same way. Null for enterprise or when neither is active.
 */
export const useCertificatePromoVariables = (
  certSlug: string,
): PromoVariables | null => {
  const perSlugFlag = promoFlagKey(certSlug)
  const isEnterprise = useIsEnterpriseMember()

  const siteWide = useSiteWidePromoVariables()
  const perSlugBucket = useBucket(perSlugFlag)
  const perSlugVariables =
    useExperimentVariables<Record<string, unknown>>(perSlugFlag)

  if (!certSlug || isEnterprise) return null

  return (
    siteWide ??
    parsePromoVariables(perSlugFlag, perSlugBucket, perSlugVariables)
  )
}
